import { Trans, useLingui } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updateAddress,
  usePatientInformationFields,
} from "@/api/Patients";
import Form from "@/components/Form/Form";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import {
  formatAddress,
  type IAddressWithCoordinates,
} from "@medoma/models/addresses";
import TextArea from "@/components/TextArea/TextArea";
import { conventionalPostalCodeSchema } from "@medoma/models/postalCode";
import { useUserProfile } from "@/api/Users";

export const EditKeyToPatient = ({
  currentAddress,
  onSuccess,
  patientId,
}: {
  currentAddress: IAddressWithCoordinates;
  onSuccess: () => void;
  patientId: string;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    setError,
    reset,
  } = useForm<IAddressWithCoordinates>({
    defaultValues: {
      keyToPatient: currentAddress.keyToPatient,
    },
  });

  const queryClient = useQueryClient();
  const { countryCode } = useUserProfile();

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (address: IAddressWithCoordinates) =>
      updateAddress(patientId, address),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const postalCode = conventionalPostalCodeSchema.shape[countryCode].parse(
      currentAddress.postalCode,
    );

    return mutate(
      formatAddress({
        ...currentAddress,
        keyToPatient: formData.keyToPatient,
        countryCode,
        postalCode,
      }),
    );
  });

  const { t } = useLingui();

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  return (
    <Form onSubmit={validateAndSubmit}>
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar information om nyckel`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <TextArea
            label={t`Nyckel`}
            showOptionalLabel={
              conditionalInformationFields.KeyToPatient ===
              informationFieldConfigValueSchema.enum.optional
            }
            {...register("keyToPatient", {
              required: {
                value:
                  conditionalInformationFields.KeyToPatient ===
                  informationFieldConfigValueSchema.enum.required,
                message: t`Ange information om nyckel`,
              },
            })}
            formatHint={t`T.ex nyckelskåp, hos granne`}
            errorMessage={errors.keyToPatient?.message}
          />

          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
